import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';

import { Loader } from '@/components/core/Loader';
import { getInjector } from '@/services/angularjs-bridge';
import { Organization } from '@/types/Organization';

interface OrganizationContextInterface {
    organization?: Organization;
}

const OrganizationContext = createContext<OrganizationContextInterface | undefined>(undefined);

const useOrganization = () => {
    const organizationContext = useContext(OrganizationContext);

    if (!organizationContext) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }

    return organizationContext;
};

/**
 * An organization is a recipient of shared items when its item namespace
 * belongs to a *different* organization (same rule as `Organization#isSharedItemsRecipient`
 * on the server).
 */
const isSharedItemsRecipient = (organization?: Organization): boolean => {
    const itemNamespaceOrganizationId = organization?.itemNamespaceOrganizationId;
    return itemNamespaceOrganizationId != null && String(itemNamespaceOrganizationId) !== String(organization?.id);
};

const OrganizationProvider = ({ children }: PropsWithChildren) => {

    const [organization, setOrganization] = useState<Organization | undefined>(undefined);

    useEffect(() => {
        // Grab $rootScope from the angularjs world
        const angularJSInjector = getInjector();
        const $rootScope = angularJSInjector.get('$rootScope');
        setOrganization($rootScope.organization);
    }, []);

    if (!organization) {
        return <Loader />;
    } else {
        return <OrganizationContext.Provider value={{ organization }}>{children}</OrganizationContext.Provider>;
    }

};

export { isSharedItemsRecipient, OrganizationContext, OrganizationProvider, useOrganization };
